import React, {useEffect, useState} from 'react'
import './styles.css'
import {useTranslation} from "react-i18next";
import {IconButton, Text} from "atmosfair-ui"
import {ACTIVITY_TYPE, AllActivityTypesArray, TRAVEL_OPTION_TYPE} from "../../../../types-enums/activity";
import {getCo2EForDayOfJourney, getCo2KgEForTravelOptionType} from "../../../../utils/getter/co2-getter";
import {CommonContextProps, JourneyDayCardContext, JourneyDayCardContextProps, JourneyDayCardProps} from "../../common";
import JourneyDayCardItems from "../journey-day-card-items/JourneyDayCardItems";
import {WhichArrowProps} from "../../../../container/journey/journey-dnd-container/JourneyDnDContainer";
import {useDroppable} from "@dnd-kit/core";
import clsx from "clsx";
import "../../styles.css"
import {DISPLAY_ORDER_TRAVEL_OPTIONS} from "../../../../utils/common";
import {Tooltip} from "@mui/material";
import EmissionsPerTravelOptionTooltip
    from "../../../../molecules/emissions-per-travel-option-tooltip/EmissionsPerTravelOptionTooltip";
import {theme} from "../../../../styles/theme";
import {roundRelativeNumber} from "../../../../utils/math/round-values";
import {DurationProps} from "../../../../types-enums/activity-common-properties";
import {isActivityCarBusState} from "../../../../utils/check/type-guards";
import {COMMON_COLOR, setForbiddenDaysToWhichActivityCannotDraggedAndDropped} from "../common";
import {generateWhyDroppableDisabledString} from "../../common/common";
import {formatCo2ETotalString} from "../../../../utils/string/common";

export interface JourneyOverviewDayCardProps extends JourneyDayCardProps {
    onDeleteDay: (day: number) => void,
    onMouseEnter: (groupId: string | null) => void,
    onMouseLeave: (groupId: string | null) => void,
    hoveredId: string | null,
    altKeyPressed: boolean,
    activitiesAtDay: AllActivityTypesArray,
    onDayArrow: (day: number, whichArrow: WhichArrowProps) => void,
    hasExactDates: boolean,
    travelComponentActivities?: AllActivityTypesArray
    journeyDuration: DurationProps,
    journeyPersonCount: number,
}

/**
 * Conditionally disables the droppable zone (no dropping of dragged element possible), considering the activityType
 * and the day. A hotel cannot be dropped on the last day of a journey, because hotel stays are always overnight,
 * if an Activity is moved in group, the day at which the dragging started, the duration of the journey and the
 * Activity duration have to be taken into consideration when allowing or disallowing dropping.
 */
const getForbiddenDaysToMoveActivityTo = (
    activity: CommonContextProps['draggingActivity'],
    activities: AllActivityTypesArray,
    journeyDurationInDays: JourneyDayCardProps["journeyDurationInDays"],
    altKeyPressed: boolean,
) => {
    const forbiddenDays: number[] = []
    if (activity && typeof journeyDurationInDays !== 'undefined') {
        const {common: {activityType}} = activity
        switch (activityType) {
            case ACTIVITY_TYPE.hotel:
                if (altKeyPressed) {
                    forbiddenDays.push(journeyDurationInDays)
                } else {
                    setForbiddenDaysToWhichActivityCannotDraggedAndDropped(activity, activities, journeyDurationInDays, forbiddenDays)
                }
                break
            case ACTIVITY_TYPE.flight:
                break;
            case ACTIVITY_TYPE.car_bus:
                if (!altKeyPressed) setForbiddenDaysToWhichActivityCannotDraggedAndDropped(activity, activities, journeyDurationInDays, forbiddenDays)
                break;
            default:
                activityType satisfies never;
        }
    }
    return forbiddenDays
}

const setDisableWithHotel = (journeyDurationInDays: number | undefined, allActivities: AllActivityTypesArray, day: number | null) => {
    let hasHotelOnSecondLastDay = false
    if (typeof journeyDurationInDays !== 'undefined') {
        const secondLastDay = journeyDurationInDays - 1
        if (day === secondLastDay) hasHotelOnSecondLastDay = Boolean(allActivities
            ?.filter(activity => activity.common.day === secondLastDay)
            .some(activity => activity.common.activityType === ACTIVITY_TYPE.hotel))
    }
    return hasHotelOnSecondLastDay
}

const setDisableWithCarBusRoute = (activitiesAtDay: AllActivityTypesArray) => {
    return activitiesAtDay
        .filter(activityAtDay => activityAtDay.common.activityType === ACTIVITY_TYPE.car_bus)
        .some(activityAtDay => isActivityCarBusState(activityAtDay) && activityAtDay.route !== null)
}

export const JOURNEY_DAY_CARD_SCROLL_TO_ID = "journey-day-card-"

const JourneyDayCard = ({
                            activitiesAtDay,
                            allActivities,
                            droppableZoneId,
                            day,
                            onActivityArrowUp,
                            onActivityArrowDown,
                            onActivityDelete,
                            isRecalculatingActivityEmissions,
                            onDeleteDay,
                            journeyDurationInDays,
                            onMouseEnter,
                            onMouseLeave,
                            hoveredId,
                            altKeyPressed,
                            onDayArrow,
                            hasExactDates,
                            draggingActivity,
                            travelComponentActivities,
                            journeyDuration,
                            journeyPersonCount,
                        }: JourneyOverviewDayCardProps) => {
    const [forbiddenDaysToDragAndDropActivityTo, setForbiddenDaysToDragAndDropActivityTo] = useState<number[]>(getForbiddenDaysToMoveActivityTo(draggingActivity, allActivities, journeyDurationInDays, altKeyPressed))
    const droppableDayDisabled = forbiddenDaysToDragAndDropActivityTo.includes(day)
    const [journeyOverviewDayCardContext, setJourneyOverviewDayCardContext] = useState<JourneyDayCardContextProps>({
        activitiesAtDay,
        droppableZoneId,
        day,
        onActivityArrowUp,
        onActivityArrowDown,
        onActivityDelete,
        isRecalculatingActivityEmissions,
        onDeleteDay,
        journeyDurationInDays,
        onMouseEnter,
        onMouseLeave,
        hoveredId,
        altKeyPressed,
        hasExactDates,
        draggingActivity,
        journeyDuration,
        allActivities,
        journeyPersonCount,
    })
    const hasHotelOnSecondLastDay = setDisableWithHotel(journeyDurationInDays, allActivities, day)
    const hasCarBusWithRoute = setDisableWithCarBusRoute(activitiesAtDay)
    const {setNodeRef, isOver, active} = useDroppable({id: droppableZoneId, disabled: droppableDayDisabled})
    const {i18n, t} = useTranslation();
    const {resolvedLanguage} = i18n
    const co2ForActivitiesAtDay = getCo2EForDayOfJourney(allActivities, day, true)

    useEffect(() => {
        setForbiddenDaysToDragAndDropActivityTo(getForbiddenDaysToMoveActivityTo(draggingActivity, allActivities, journeyDurationInDays, altKeyPressed))
    }, [draggingActivity]);

    useEffect(() => {
        setJourneyOverviewDayCardContext({
            activitiesAtDay,
            droppableZoneId,
            day,
            onActivityArrowUp,
            onActivityArrowDown,
            onActivityDelete,
            isRecalculatingActivityEmissions,
            onDeleteDay,
            journeyDurationInDays,
            onMouseEnter,
            onMouseLeave,
            hoveredId,
            altKeyPressed,
            hasExactDates,
            draggingActivity,
            travelComponentActivities,
            journeyDuration,
            allActivities,
            journeyPersonCount
        })
    }, [
        activitiesAtDay,
        droppableZoneId,
        day,
        onActivityArrowUp,
        onActivityArrowDown,
        onActivityDelete,
        isRecalculatingActivityEmissions,
        onDeleteDay,
        journeyDurationInDays,
        onMouseEnter,
        onMouseLeave,
        hoveredId,
        hasExactDates,
        draggingActivity,
        altKeyPressed,
        travelComponentActivities,
        journeyDuration,
        allActivities,
        journeyPersonCount
    ])

    const _conditionallyGenerateTooltip = () => {
        if (hasCarBusWithRoute) return 'HINTS.JOURNEY.ARROW_DISABLED_CAR_BUS_WITH_ROUTE_AT_DAY'
        if (hasHotelOnSecondLastDay) return 'HINTS.JOURNEY.ARROW_DOWN_HOTEL_DAY_DISABLED'
        return 'TOOLTIP.JOURNEY.MOVE_DAY_DOWN'
    }

    const _renderTravelOptionTypeHeadlineText = (text: string) => <Text
        style={{marginBottom: theme.spacer.medium}}
        variant={"headlineSmall"}
        color={COMMON_COLOR}>
        {text}
    </Text>

    const _renderContent = () => DISPLAY_ORDER_TRAVEL_OPTIONS.map((travelOptionType, index) => {
            if (travelOptionType === TRAVEL_OPTION_TYPE.undertaking) return null
            const co2ForTravelOptionType = getCo2KgEForTravelOptionType(activitiesAtDay, travelOptionType, true)
            let roundedRelativeValue = ''
            if (co2ForActivitiesAtDay > 0) {
                roundedRelativeValue = `(${roundRelativeNumber((co2ForTravelOptionType / co2ForActivitiesAtDay) * 100)}%)`
            }
            return <div
                style={{marginBottom: 1.5 * theme.spacer.veryLarge}}
                key={index}>
                <div className={"journey-day-card-travel-option-headline-wrapper"}>
                    {_renderTravelOptionTypeHeadlineText(`GENERAL.${travelOptionType.toUpperCase()}`)}
                    {_renderTravelOptionTypeHeadlineText(`${formatCo2ETotalString(co2ForTravelOptionType, resolvedLanguage)} ${roundedRelativeValue}`)}
                </div>
                <JourneyDayCardItems travelOptionType={travelOptionType}
                />
            </div>
        }
    )

    const _renderDayCardHeadlineText = (text: string) => <Text
        variant={"headlineMedium"}
        color={COMMON_COLOR}>{text}
    </Text>

    return <JourneyDayCardContext.Provider value={journeyOverviewDayCardContext}>
        <li
            ref={setNodeRef}
            id={`${JOURNEY_DAY_CARD_SCROLL_TO_ID}${day}`}
            className={"journey-day-card-container"}>
            <div className={"journey-day-card-title-row"}>
                {_renderDayCardHeadlineText(`${t('GENERAL.DAY')} ${day}`)}
                <div className={'journey-day-card-text-icon-wrapper'}>
                    <Tooltip placement={"top"} title={co2ForActivitiesAtDay === 0 ? null :
                        <EmissionsPerTravelOptionTooltip
                            totalEmissions={co2ForActivitiesAtDay}
                            activities={activitiesAtDay}/>}>
                        <div>
                            {_renderDayCardHeadlineText(`${t('GENERAL.TOTAL')}: ${formatCo2ETotalString(co2ForActivitiesAtDay, resolvedLanguage)}`)}
                        </div>
                    </Tooltip>
                    <IconButton
                        disabled={day === 1 || isRecalculatingActivityEmissions}
                        tooltip={'TOOLTIP.JOURNEY.MOVE_DAY_UP'}
                        size={"medium"}
                        color={COMMON_COLOR}
                        icon={"arrowUp"}
                        onClick={() => onDayArrow(day, "up")}/>
                    <IconButton
                        disabled={hasHotelOnSecondLastDay || hasCarBusWithRoute || day === journeyDurationInDays || isRecalculatingActivityEmissions}
                        tooltip={_conditionallyGenerateTooltip()}
                        size={"medium"}
                        color={COMMON_COLOR}
                        icon={"arrowDown"}
                        onClick={() => onDayArrow(day, "down")}/>
                    <IconButton
                        disabled={isRecalculatingActivityEmissions}
                        tooltip={'TOOLTIP.JOURNEY.DELETE_DAY'}
                        size={"medium"}
                        color={COMMON_COLOR}
                        icon={"trash"}
                        onClick={() => onDeleteDay(day)}/>
                </div>
            </div>
            <div className={clsx(
                'journey-day-card-travel-options-selections-wrapper',
                isOver && active?.data.current?.droppableZoneId !== droppableZoneId && 'dnd-day-card-is-over')}>
                {_renderContent()}
            </div>
            {droppableDayDisabled && <div className={'journey-day-card-droppable-disabled-overlay'}>
                <Text
                    weight={"bold"}
                    classes={'journey-day-card-droppable-disabled-overlay-text'}>{draggingActivity && generateWhyDroppableDisabledString(
                    draggingActivity,
                    day,
                    i18n,
                    journeyDurationInDays
                )}</Text>
            </div>}
        </li>
    </JourneyDayCardContext.Provider>
}

export default JourneyDayCard